<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.gamification_items', 2),
                        href: meta.links.items_index,
                    },
                    {
                        name: $tc('messages.gamification_trades', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <NavLinks :links="meta.navGroupLinks" />
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-3">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                selects.students.length
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="">
                        <FormLabel :value="$t('messages.status')" for="status" />
                        <FormSelect
                            :null-option-text="$t('messages.all')"
                            id="status"
                            v-model="form.status"
                            :options="options.status"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.status" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 flex space-x-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState picture v-if="trades.data.length === 0" />
        <div class="md:grid md:grid-cols-2 md:gap-6 mt-5" v-else>
            <CardPanel v-for="trade in trades.data" :key="trade.id">
                <div class="md:flex gap-x-2">
                    <div>
                        <div>
                            {{ trade.date_display }}
                        </div>
                        <div>
                            <div class="flex md:justify-center">
                                <IconArrowsLeftRight
                                    class="w-16 h-16"
                                    :class="{
                                        'text-indigo-200': trade.status === null,
                                        'text-green-200': trade.status === 'finished',
                                        'text-yellow-200': trade.status === 'error',
                                    }"
                                />
                            </div>
                        </div>
                        <div>
                            <BadgePill v-if="trade.status === null" color="indigo">
                                {{ $t('messages.created') }}
                            </BadgePill>
                            <BadgePill v-else-if="trade.status === 'finished'" color="green">
                                {{ $t('messages.completed') }}
                            </BadgePill>
                            <BadgePill v-else-if="trade.status === 'error'" color="yellow">
                                {{ $t('messages.error') }}:
                                {{ $t('messages.quantity') }}
                            </BadgePill>
                        </div>
                        <div class="mt-3 flex gap-x-2">
                            <Tippy>
                                <button>
                                    <IconInfoCircle class="h-6 w-6 text-gray-400" />
                                </button>
                                <template #content>
                                    {{ $t('messages.trade_explanation') }}
                                </template>
                            </Tippy>
                            <Tippy v-if="trade.balance.is_gift">
                                <button>
                                    <IconGift class="h-6 w-6 text-gray-400" />
                                </button>
                                <template #content>
                                    {{ $t('messages.trade_gift_explanation') }}
                                </template>
                            </Tippy>
                            <Tippy v-if="trade.balance.imbalance">
                                <button>
                                    <IconScaleOff class="h-6 w-6 text-gray-400" />
                                </button>
                                <template #content>
                                    {{ $t('messages.trade_is_imbalanced') }}
                                </template>
                            </Tippy>
                        </div>
                        <div>
                            <InertiaSquareLinkButton action="show" :href="trade.links.show" />
                        </div>
                    </div>
                    <AppWell class="flex-1">
                        <AppCard>
                            <div class="flex gap-x-2 md:gap-x-4">
                                <img
                                    class="w-16 h-16 rounded-full"
                                    :src="trade.first.avatar_or_profile_photo"
                                    :title="trade.first.first_name_and_initial"
                                    alt="Avatar"
                                />
                                <div>
                                    <div class="mt-1 rounded-md shadow-sm" v-if="trade.settings.coins.first">
                                        <img
                                            class="h-6 w-6 mr-1"
                                            src="/assets/gamification/currency/coin.svg"
                                            alt="emerald"
                                        />
                                        <div>
                                            {{ trade.settings.coins.first }}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        <TradeElementsDisplay
                                            side="first"
                                            :elements="options.items"
                                            :selected="trade.settings.items"
                                            element-class="h-12 w-12"
                                        />
                                    </div>
                                    <div class="mt-2">
                                        <TradeElementsDisplay
                                            side="first"
                                            :elements="options.cards"
                                            :selected="trade.settings.cards"
                                            element-class="h-12 w-12"
                                        />
                                    </div>
                                </div>
                            </div>
                        </AppCard>
                        <AppCard class="mt-3">
                            <div class="flex gap-x-2 md:gap-x-4">
                                <img
                                    class="w-16 h-16 rounded-full"
                                    :src="trade.second.avatar_or_profile_photo"
                                    :title="trade.second.first_name_and_initial"
                                    alt="Avatar"
                                />
                                <div>
                                    <div class="mt-1 rounded-md shadow-sm" v-if="trade.settings.coins.second">
                                        <img
                                            class="h-6 w-6 mr-1"
                                            src="/assets/gamification/currency/coin.svg"
                                            alt="emerald"
                                        />
                                        <div>
                                            {{ trade.settings.coins.second }}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        <TradeElementsDisplay
                                            side="second"
                                            :elements="options.items"
                                            :selected="trade.settings.items"
                                            element-class="h-12 w-12"
                                        />
                                    </div>
                                    <div class="mt-2">
                                        <TradeElementsDisplay
                                            side="second"
                                            :elements="options.cards"
                                            :selected="trade.settings.cards"
                                            element-class="h-12 w-12"
                                        />
                                    </div>
                                </div>
                            </div>
                        </AppCard>

                        <div v-if="trade.balance.imbalance" class="mt-3">
                            <ProgressBar :percent="trade.balance.percent" :show-text="false" color="gray" />
                        </div>
                    </AppWell>
                </div>
                <AlertMessage v-if="trade.status === 'error'" class="mt-3">
                    <template v-slot:body>
                        {{ trade.trade_error_explanation }}
                    </template>
                </AlertMessage>
            </CardPanel>
        </div>
        <template v-slot:pagination>
            <InertiaPagination :paginator="trades" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import CardPanel from '@/Shared/CardPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconArrowsLeftRight, IconGift, IconInfoCircle, IconScaleOff } from '@tabler/icons-vue';
import TradeElementsDisplay from '@/components/TradeElementsDisplay.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import NavLinks from '@/components/NavLinks.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import { Tippy } from 'vue-tippy';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        InertiaSquareLinkButton,
        ProgressBar,
        FormError,
        FormSelect,
        FormLabel,
        NavLinks,
        AlertMessage,
        InertiaPagination,
        AppWell,
        AppCard,
        TradeElementsDisplay,
        EmptyState,
        PanelPage,
        AppButton,
        CardPanel,
        BadgePill,
        Tippy,
        IconArrowsLeftRight,
        IconInfoCircle,
        IconScaleOff,
        IconGift,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'trades', 'filters', 'gamificationSet', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                status: this.filters.status,
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
